import React, { useEffect, useMemo, useState } from 'react';
import Header from '../components/Header';
import {
    Box,
    Flex,
    Text,
    Center,
    Spinner,
    IconButton,
    Tooltip,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    Button,
} from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { FiRefreshCw } from 'react-icons/fi';
import { useSearchParams } from 'react-router-dom';
import moment from 'moment';
import { MonthPicker } from '../components/MonthPicker';
import API from '../helpers/axiosInstance';
import { filterByPermission } from '../utils/DefaultFunction';
import BoxWrapper from '../components/BoxWrapper/BoxWrapper';

import OverView from './DashboardComponents/OverView/OverView';
import UsageAnalytics from './DashboardComponents/UsageAnalytics/UsageAnalytics';
import Performance from './DashboardComponents/Performance/Performance';
import Savings from './DashboardComponents/Savings/Savings';
import Template from './DashboardComponents/Templates/Template';
import EventsTracking from './DashboardComponents/EventsTracking/EventsTracking';
import FallbackPerformanceAnalysis from './DashboardComponents/FallBack/FallbackPerformanceAnalysis';
import StoryInsightsTab from '../components/story-dashboard/StoryInsightsTab';
import DemographicsTab from '../components/story-dashboard/DemographicsTab';

const MAIN_TAB_PARAM = 'tab';
const SUB_TAB_PARAM = 'sub';

const messagingSubTabs = [
    { slug: 'dashboard-usage',        name: 'usage',        label: 'Usage Analytics',       component: UsageAnalytics },
    { slug: 'dashboard-performance',  name: 'performance',  label: 'Performance',           component: Performance },
    { slug: 'dashboard-saving',       name: 'savings',      label: 'Savings Analytics',     component: Savings },
    { slug: 'dashboard-template',     name: 'templates',    label: 'Templates',             component: Template },
    { slug: 'dashboard-fallback',     name: 'fallback',     label: 'Fallback Performance',  component: FallbackPerformanceAnalysis },
];

const standaloneTabDefs = [
    { slug: 'dashboard-overview', name: 'overview',        label: 'Overview',        component: OverView },
    { slug: 'dashboard-event',    name: 'events-tracking', label: 'Events Tracking', component: EventsTracking },
];

const OUTER_TABS = [
    { name: 'messaging',      label: 'Messaging' },
    { name: 'story-insights', label: 'Story Insights' },
    { name: 'demographics',   label: 'Demographics' },
];

const TAB_STYLES = {
    base: {
        px: '4',
        py: '1.5',
        borderRadius: 'full',
        fontSize: 'sm',
        fontWeight: '500',
        cursor: 'pointer',
        transition: 'all 0.15s',
        bg: 'transparent',
        color: 'gray.600',
        border: 'none',
    },
    active: {
        bg: 'white',
        boxShadow: 'sm',
        fontWeight: '600',
        color: 'gray.800',
    },
};

const Dashboard = ({ header }) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [date, setDate] = useState(moment());
    const [permissions, setPermissions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshKey, setRefreshKey] = useState(0);
    const [refreshOnce, setRefreshOnce] = useState(false);

    const refresh = refreshOnce
        ? '&refresh=1'
        : searchParams.get('refresh') ? `&refresh=${searchParams.get('refresh')}` : '';

    useEffect(() => { setRefreshOnce(false); }, [refreshKey]);

    const mainTab = searchParams.get(MAIN_TAB_PARAM) || 'overview';
    const subTab  = searchParams.get(SUB_TAB_PARAM)  || '';

    const setMainTab = (name) => {
        const p = new URLSearchParams(searchParams);
        p.set(MAIN_TAB_PARAM, name);
        p.delete(SUB_TAB_PARAM);
        setSearchParams(p);
    };

    const setSubTab = (name) => {
        const p = new URLSearchParams(searchParams);
        p.set(MAIN_TAB_PARAM, 'messaging');
        p.set(SUB_TAB_PARAM, name);
        setSearchParams(p);
    };

    const getApiHeaders = () => ({
        'x-api-key': localStorage.getItem('api-key'),
        'x-access-token': localStorage.getItem('customer-portal'),
    });

    const fetchPermissions = async () => {
        setLoading(true);
        try {
            const url = `${process.env.REACT_APP_API_URL}/permission/feature/list?slug=dashboard${refresh}`;
            const response = await API.get(url, { headers: getApiHeaders() });
            setPermissions(response.data.data || []);
        } catch {
            setPermissions([]);
        } finally {
            setLoading(false);
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { fetchPermissions(); }, []);

    const availableSubTabs = useMemo(() => {
        if (loading) return [];
        return filterByPermission(messagingSubTabs, permissions);
    }, [permissions, loading]);

    const availableStandaloneTabs = useMemo(() => {
        if (loading) return [];
        return filterByPermission(standaloneTabDefs, permissions);
    }, [permissions, loading]);

    const activeSubTab = useMemo(() => {
        if (!availableSubTabs.length) return null;
        return availableSubTabs.find(t => t.name === subTab) || availableSubTabs[0];
    }, [availableSubTabs, subTab]);

    const disableFutureMonths = (m) => m.isAfter(moment(), 'month');

    if (loading) {
        return (
            <>
                <Header header={header} />
                <BoxWrapper>
                    <Center h="400px"><Spinner size="xl" color="blue.500" thickness="4px" /></Center>
                </BoxWrapper>
            </>
        );
    }

    const ActiveMessagingComponent = activeSubTab?.component;

    return (
        <>
            <Header header={header} />
            <BoxWrapper>

                {/* Page header */}
                <Flex justifyContent="space-between" align="center" my={2}>
                    <Flex direction="column">
                        <Text fontSize="2xl" fontWeight="semibold" color="gray.700">Dashboard</Text>
                        <Text fontSize="sm" color="#64748b">
                            Monitor your account performance, usage metrics, and cost savings
                        </Text>
                    </Flex>
                    <Flex align="center" gap={2}>
                        <MonthPicker value={date} onChange={setDate} disabledDates={disableFutureMonths} />
                        <Tooltip label="Refresh data">
                            <IconButton
                                icon={<FiRefreshCw />}
                                size="sm"
                                variant="outline"
                                aria-label="Refresh"
                                onClick={() => { setRefreshOnce(true); setRefreshKey(k => k + 1); }}
                            />
                        </Tooltip>
                    </Flex>
                </Flex>

                {/* Outer tab bar */}
                <Flex
                    bg="#f1f5f9"
                    p="1"
                    borderRadius="full"
                    mb={4}
                    w="100%"
                >
                    {/* Overview standalone tab */}
                    {availableStandaloneTabs.find(t => t.name === 'overview') && (
                        <Box
                            as="button"
                            flex={1}
                            py={1.5}
                            borderRadius="full"
                            fontSize="sm"
                            fontWeight={mainTab === 'overview' ? '600' : '500'}
                            bg={mainTab === 'overview' ? 'white' : 'transparent'}
                            color={mainTab === 'overview' ? 'gray.800' : 'gray.500'}
                            boxShadow={mainTab === 'overview' ? 'sm' : 'none'}
                            _hover={{ color: 'gray.700' }}
                            transition="all 0.15s"
                            onClick={() => setMainTab('overview')}
                        >
                            Overview
                        </Box>
                    )}

                    {/* Messaging dropdown tab */}
                    <Menu placement="bottom-start">
                        <MenuButton
                            as={Button}
                            flex={1}
                            py={1.5}
                            h="auto"
                            borderRadius="full"
                            fontSize="sm"
                            fontWeight="500"
                            bg={mainTab === 'messaging' ? 'white' : 'transparent'}
                            color={mainTab === 'messaging' ? 'gray.800' : 'gray.500'}
                            boxShadow={mainTab === 'messaging' ? 'sm' : 'none'}
                            _hover={{ bg: mainTab === 'messaging' ? 'white' : 'transparent', color: 'gray.700' }}
                            _active={{ bg: mainTab === 'messaging' ? 'white' : 'transparent' }}
                            variant="unstyled"
                            display="inline-flex"
                            alignItems="center"
                            justifyContent="center"
                        >
                            <Flex align="center" justify="center" gap={1}>
                                {mainTab === 'messaging' && activeSubTab
                                    ? `Messaging · ${activeSubTab.label}`
                                    : 'Messaging'}
                                <ChevronDownIcon />
                            </Flex>
                        </MenuButton>
                        <MenuList minW="180px" shadow="md" borderRadius="lg" py={1}>
                            {availableSubTabs.length === 0 ? (
                                <MenuItem isDisabled fontSize="sm" color="gray.400">No access</MenuItem>
                            ) : (
                                availableSubTabs.map(t => (
                                    <MenuItem
                                        key={t.name}
                                        fontSize="sm"
                                        fontWeight={activeSubTab?.name === t.name && mainTab === 'messaging' ? '500' : '400'}
                                        color={activeSubTab?.name === t.name && mainTab === 'messaging' ? 'blue.600' : 'gray.700'}
                                        bg={activeSubTab?.name === t.name && mainTab === 'messaging' ? 'blue.50' : 'transparent'}
                                        onClick={() => setSubTab(t.name)}
                                        _hover={{ bg: 'gray.50' }}
                                    >
                                        {t.label}
                                    </MenuItem>
                                ))
                            )}
                        </MenuList>
                    </Menu>

                    {/* Story Insights tab */}
                    <Box
                        as="button"
                        flex={1}
                        py={1.5}
                        borderRadius="full"
                        fontSize="sm"
                        fontWeight="500"
                        bg={mainTab === 'story-insights' ? 'white' : 'transparent'}
                        color={mainTab === 'story-insights' ? 'gray.800' : 'gray.500'}
                        boxShadow={mainTab === 'story-insights' ? 'sm' : 'none'}
                        _hover={{ color: 'gray.700' }}
                        transition="all 0.15s"
                        onClick={() => setMainTab('story-insights')}
                    >
                        Stories
                    </Box>

                    {/* Demographics tab */}
                    <Box
                        as="button"
                        flex={1}
                        py={1.5}
                        borderRadius="full"
                        fontSize="sm"
                        fontWeight="500"
                        bg={mainTab === 'demographics' ? 'white' : 'transparent'}
                        color={mainTab === 'demographics' ? 'gray.800' : 'gray.500'}
                        boxShadow={mainTab === 'demographics' ? 'sm' : 'none'}
                        _hover={{ color: 'gray.700' }}
                        transition="all 0.15s"
                        onClick={() => setMainTab('demographics')}
                    >
                        Demographics
                    </Box>

                    {/* Events Tracking standalone tab */}
                    {availableStandaloneTabs.find(t => t.name === 'events-tracking') && (
                        <Box
                            as="button"
                            flex={1}
                            py={1.5}
                            borderRadius="full"
                            fontSize="sm"
                            fontWeight={mainTab === 'events-tracking' ? '600' : '500'}
                            bg={mainTab === 'events-tracking' ? 'white' : 'transparent'}
                            color={mainTab === 'events-tracking' ? 'gray.800' : 'gray.500'}
                            boxShadow={mainTab === 'events-tracking' ? 'sm' : 'none'}
                            _hover={{ color: 'gray.700' }}
                            transition="all 0.15s"
                            onClick={() => setMainTab('events-tracking')}
                        >
                            Events Tracking
                        </Box>
                    )}
                </Flex>

                {/* Tab content */}
                <Box>
                    {mainTab === 'overview' && (
                        <OverView key={refreshKey} date={date} refresh={refresh} />
                    )}

                    {mainTab === 'messaging' && (
                        availableSubTabs.length === 0 ? (
                            <Center h="300px">
                                <Box textAlign="center">
                                    <Text fontSize="xl" fontWeight="semibold" color="gray.700" mb={2}>No Access</Text>
                                    <Text fontSize="sm" color="gray.500">You don't have permission to view any dashboard sections.</Text>
                                </Box>
                            </Center>
                        ) : ActiveMessagingComponent ? (
                            <ActiveMessagingComponent key={refreshKey} date={date} refresh={refresh} />
                        ) : null
                    )}

                    {mainTab === 'story-insights' && <StoryInsightsTab />}
                    {mainTab === 'demographics' && <DemographicsTab />}

                    {mainTab === 'events-tracking' && (
                        <EventsTracking key={refreshKey} date={date} refresh={refresh} />
                    )}
                </Box>

            </BoxWrapper>
        </>
    );
};

export default Dashboard;
